'use client';
import {
  Box,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import BallonPaymentButtons from '@/app/finance/_components/BallonPaymentButtons';
import MonthlyPaymentTermButtons from '@/app/finance/_components/MonthlyPaymentTermButtons';
import {
  DEFAULT_BALLOON,
  DEFAULT_BALLOON_RANGE,
  DEFAULT_INTEREST,
  MAX_INTEREST,
  MIN_INTEREST,
  MONTHLYPAYMENTTERM,
  SLIDER_MODE1,
  SLIDER_STEP_MODE_5000,
} from '@/constants/payment-method/payment.constants';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useFinance } from '@/hooks/finance/finance.hook';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import {
  DefaultRepaymentElement,
  DefaultRepaymentValue,
  getInstallementValue,
} from '@/services/new-cars/new-cars.service';
import { VehicleExtended } from '@/types/details-car/details-car.types';
import { BalloonTypes } from '@/types/filters/filters.types';

import Styles from './FinanceOption.module.scss';

interface FinanceOptionProps {
  vehicle?: VehicleExtended;
  defaultValues: DefaultRepaymentValue;
}

const FinanceOption: React.FC<FinanceOptionProps> = ({
  vehicle,
  defaultValues,
}) => {
  const router = useRouter();
  const { setSelectedFinance } = useFinance();
  const { defaultPriceFilter } = usePriceFilter();

  const defaultCalulation = {
    monthlyPayment: 0,
    totalRepayment: 0,
    fees: 0,
    registration_fee: 0,
    deposit: 0,
    loanAmount: 0,
    totalInterest: 0,
    estimatedPayoffDate: '',
    effectiveAnnualRate: '',
  };

  const [activeBalloonOption, setActiveBalloonOption] = useState<'%' | 'R'>(
    '%',
  );
  const depositMaxValue = defaultPriceFilter.max * 0.7;
  const isPersentageMode = activeBalloonOption === '%';
  const vehiclePrice =
    Number(vehicle?.nowPrice) > 0 ? vehicle?.nowPrice : vehicle?.price;
  const vehicleYear = Number(vehicle?.year);
  const yearDefaults = defaultValues?.find((v) => {
    const currentYear = new Date().getFullYear();
    const offset = vehicleYear ? vehicleYear - currentYear : 0;

    return offset === v?.year_offset;
  });

  const initialFinanceFilter = {
    deposit: yearDefaults?.deposit,
    balloon: yearDefaults?.baloon_payment,
    interest: yearDefaults?.interest_rate,
    paymentTerm: yearDefaults?.term,
    price: Number(vehiclePrice),
  };
  const [financeFilter, setFinanceFilter] = useState(initialFinanceFilter);
  const [financeCalculation, setFinanceCalculation] =
    useState(defaultCalulation);

  const getBallonOption = useCallback(() => {
    return {
      prefix: activeBalloonOption ? '%' : 'R',
      max: isPersentageMode
        ? DEFAULT_BALLOON_RANGE.max
        : defaultPriceFilter.max,
      sliderStep: isPersentageMode ? SLIDER_MODE1 : SLIDER_STEP_MODE_5000,
    };
  }, [activeBalloonOption]);

  useEffect(() => {
    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      price: Number(vehiclePrice),
    }));
  }, [vehiclePrice]);

  useEffect(() => {
    calculateEstimatedPayment();
  }, [
    activeBalloonOption,
    financeFilter.price,
    financeFilter.deposit,
    financeFilter.balloon,
    financeFilter.paymentTerm,
    financeFilter.interest,
  ]);

  const getInstallementValueDebounced = debounce(
    async (
      financeFilter: typeof initialFinanceFilter,
      isPercentageMode: boolean,
      defaultRepaymentValues: DefaultRepaymentElement,
    ) => {
      const backendVal = await getInstallementValue({
        financeFilter: {
          price: Number(financeFilter?.price),
          interest: financeFilter?.interest ?? DEFAULT_INTEREST,
          paymentTerm: Number(financeFilter?.paymentTerm),
          balloon: financeFilter?.balloon ?? DEFAULT_BALLOON,
          deposit: financeFilter?.deposit ?? depositMaxValue,
        },
        balloonOption: isPercentageMode
          ? BalloonTypes.percentage
          : BalloonTypes.value,
        defaultValues: defaultRepaymentValues,
      });

      if (backendVal) {
        setFinanceCalculation({
          monthlyPayment: backendVal.monthlyPayment ?? 0,
          totalRepayment: backendVal.totalRepayment ?? 0,
          fees: backendVal.fees ?? 0,
          registration_fee: backendVal.registration_fee ?? 0,
          deposit: financeFilter.deposit || 0,
          loanAmount: backendVal.loanAmount ?? 0,
          totalInterest: backendVal.totalInterest ?? 0,
          estimatedPayoffDate: backendVal.estimatedPayoffDate ?? '',
          effectiveAnnualRate: backendVal.effectiveAnnualRate ?? '',
        });
      }
      return backendVal;
    },
    1000,
  );

  const calculateEstimatedPayment = async () => {
    if (!yearDefaults) return;

    const isPercentageMode = activeBalloonOption === '%';

    try {
      await getInstallementValueDebounced(
        financeFilter,
        isPercentageMode,
        yearDefaults,
      );
    } catch (error) {
      console.error('Error calculating payment:', error);
      setFinanceCalculation(defaultCalulation);
    }
  };

  const handlePaymentTermChange = (value: string) => {
    setFinanceFilter((prev) => ({ ...prev, paymentTerm: Number(value) }));
  };

  const handleDepositChange = (value: number) => {
    if (+value > depositMaxValue || +value < defaultPriceFilter.min) {
      return;
    }
    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      deposit: +value,
    }));
  };

  const handleBalloonChange = (value: number) => {
    const limits =
      activeBalloonOption === '%' ? DEFAULT_BALLOON_RANGE : defaultPriceFilter;

    if (value > limits.max || value < limits.min) {
      return;
    }
    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      balloon: value,
    }));
  };

  const handleInterestChange = (value: number) => {
    if (value > MAX_INTEREST || value < 0) {
      return;
    }

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      interest: value,
      price: Number(vehiclePrice),
    }));
  };

  const labelStyles = {
    mt: '3',
    fontSize: '12px',
    color: 'gray.600',
  };

  const { openLeadForm } = useFloatingCTA();

  const navigateToFinance = () => {
    const dealership = vehicle?.dealership_id;

    setSelectedFinance({
      dealerRef: dealership?.cms_new_dealer_code,
      dealerFloor: dealership?.cms_used_leads,
      vehicleImage: getImageLink(vehicle?.mainImage) || '',
      mmCode: vehicle?.mmcode || '',
      price: Number(vehiclePrice),
      used: true,
      year: Number(vehicleYear),
      make: vehicle?.brand,
      model: vehicle?.range_id,
      variant: vehicle?.model,
      mileage: vehicle?.mileage || '0',
      region: dealership?.province?.name,
      transmission: vehicle?.transmission,
    });

    router.push('/apply-finance');
  };

  return (
    <Container
      maxW={'1280'}
      width={'100%'}
      className={Styles.financeOptionWrapper}
      paddingY={{ base: 10, lg: 14 }}
      paddingX={{ base: 6, lg: 4 }}
    >
      <Box
        className={Styles.headingElement}
        marginBottom={{ base: 10, lg: 12 }}
      >
        <Heading as="h2" className={Styles.titleLg}>
          Explore your Finance options
        </Heading>
      </Box>
      <SimpleGrid columns={[1, 1, 1, 2]} spacing={{ base: 10, xl: 24 }}>
        <Stack gap={8}>
          <FormControl>
            <FormLabel className={Styles.formLabel}>Deposit</FormLabel>
            <Input
              value={`R ${financeFilter?.deposit}`}
              onChange={(e) =>
                handleDepositChange(
                  Number(e.target.value.replace(/[^0-9.-]+/g, '')),
                )
              }
              className={Styles.formInput}
              variant="flushed"
              max={depositMaxValue}
            />
          </FormControl>
          <FormControl>
            <Flex justifyContent={'space-between'}>
              <FormLabel className={Styles.formLabel}>
                Balloon Payment
              </FormLabel>
              <BallonPaymentButtons
                value={activeBalloonOption}
                onChange={setActiveBalloonOption as any}
              />
            </Flex>
            <InputGroup className={Styles.inputGroup}>
              {getBallonOption().prefix === 'R' && (
                <InputLeftAddon className={Styles.prefix}>R</InputLeftAddon>
              )}
              <Input
                value={financeFilter?.balloon}
                onChange={(e) =>
                  handleBalloonChange(
                    Number(e.target.value.replace(/[^0-9.-]+/g, '')),
                  )
                }
                className={Styles.formInput}
                variant="flushed"
                max={getBallonOption().max}
              />
              {getBallonOption().prefix === '%' && (
                <InputRightAddon className={Styles.prefix}>%</InputRightAddon>
              )}
            </InputGroup>
          </FormControl>
          <FormControl>
            <FormLabel className={Styles.formLabel}>Interest Rate</FormLabel>
            <Slider
              aria-label="Interest Rate"
              onChange={(val) => handleInterestChange(val)}
              min={MIN_INTEREST}
              max={MAX_INTEREST}
              step={0.5}
              defaultValue={financeFilter.interest}
            >
              <SliderMark value={MIN_INTEREST} ml={0} {...labelStyles}>
                {MIN_INTEREST}%
              </SliderMark>

              <SliderMark value={MAX_INTEREST} ml={'-5'} {...labelStyles}>
                {MAX_INTEREST}%
              </SliderMark>
              <SliderMark
                value={financeFilter.interest ?? DEFAULT_INTEREST}
                textAlign="center"
                color="black"
                mt="3"
                ml="-5"
                w="12"
                fontSize="16px"
                fontWeight="600"
                bg={'white'}
              >
                {financeFilter.interest}%
              </SliderMark>
              <SliderTrack bg={'gray.300'} h={'2px'}>
                <SliderFilledTrack bg={'gray.600'} />
              </SliderTrack>
              <SliderThumb
                border={2}
                borderStyle={'solid'}
                borderColor={'gray.900'}
                boxSize={4}
                _focusWithin={{
                  borderColor: 'gray.900',
                  boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
                }}
              />
            </Slider>
          </FormControl>
          <FormControl as="fieldset">
            <FormLabel as="legend" className={Styles.formLabel}>
              Monthly Payment Term
            </FormLabel>

            <MonthlyPaymentTermButtons
              value={
                financeFilter?.paymentTerm?.toString()
                  ? financeFilter?.paymentTerm?.toString()
                  : (MONTHLYPAYMENTTERM[0] as string)
              }
              onChange={handlePaymentTermChange}
            />
          </FormControl>
        </Stack>
        <Box>
          <Box className={Styles.headingElement} marginBottom={8}>
            <Text marginBottom={2}>Estimated Monthly Payment</Text>
            <Heading as="h3" className={Styles.titleLg}>
              <PriceFormat
                value={financeCalculation.monthlyPayment}
                prefix="R"
              />
            </Heading>
          </Box>
          <Box className={Styles.priceBreakdownBox}>
            <Box className={Styles.headingElement} marginBottom={'24px'}>
              <Heading as="h3" className={Styles.titleMd}>
                Price Breakdown
              </Heading>
            </Box>
            <Stack gap={4} marginBottom={8}>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Deposit</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  <PriceFormat prefix="R" value={financeFilter?.deposit ?? 0} />
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Balloon Payment</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {getBallonOption().prefix === '%' ? (
                    financeFilter?.balloon + ' %'
                  ) : (
                    <PriceFormat
                      prefix="R"
                      value={financeFilter?.balloon ?? 0}
                    />
                  )}
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Interest Rate</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {financeFilter.interest} %
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Payment Term</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {financeFilter.paymentTerm} Months
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Estimated Finance Amount</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  <PriceFormat
                    value={financeCalculation.totalRepayment}
                    prefix="R"
                  />
                </Text>
              </Flex>
            </Stack>
            <Stack gap={6}>
              <ButtonCustom
                onClick={() => {
                  openLeadForm('test-drive');
                }}
                extraClass={Styles.ctaBtn}
              >
                Book A test drive
              </ButtonCustom>
              <ButtonCustom
                className="btnOutline"
                onClick={() => navigateToFinance()}
              >
                Apply for Finance
              </ButtonCustom>
            </Stack>

            <Box marginTop={6}>
              <Popover closeOnBlur={true}>
                <PopoverTrigger>
                  <ButtonCustom className={'btnTextLink'}>
                    Disclaimer
                  </ButtonCustom>
                </PopoverTrigger>
                <Box position={'relative'}>
                  <PopoverContent borderRadius={0}>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverBody>
                      <Text>
                        All calculations made on calculators supplied on this
                        site, together with rates quoted, are guidelines only
                        and are subject to confirmation at the time of
                        finalising any transactions. All information regarding
                        the products, fees and/or costs which are included in
                        and form a fundamental basis of the calculations are
                        subject to change at any time prior to a final
                        pre-agreement quote being handed to the User.
                      </Text>
                      <Text marginTop={'12px'}>
                        {`The User indemnifies KiaRetail.co.za against any loss or
                        liability, which the User may suffer as a result of the
                        use of any calculator. The site and all information
                        provided on this site and the services provided on this
                        site, are provided "as is". The information provided on
                        this site should not be treated as professional advice`}
                        of any kind.
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Box>
              </Popover>
            </Box>
          </Box>
        </Box>
      </SimpleGrid>
    </Container>
  );
};

export default FinanceOption;
